import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteLocationNormalized } from 'vue-router'
import LoginView from '../views/login/LoginView.vue'
import MainLayout from '@/components/layout/MainLayout.vue'
import { useAuthStore } from '@/stores/auth'
import { useProjectContextStore } from '@/stores/projectContext'
import { evaluateLicenseStatus } from '@/composables/useLicenseStatus'
import LocationDetailsView from '../views/locations/LocationDetailsView.vue'
import TablesView from '../views/tables/TablesView.vue'

const router = createRouter({
  history: createWebHashHistory(''),
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView
    },
    {
      path: '/admin-register',
      name: 'admin-register',
      component: () => import('../views/login/AdminRegisterView.vue')
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/login/ForgotPasswordView.vue')
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/login/ResetPasswordView.vue')
    },
    {
      path: '/office-admin/accept-invite',
      name: 'office-admin-accept-invite',
      component: () => import('../views/login/OfficeAdminAcceptInviteView.vue')
    },
    {
      path: '/admin/accept-invite',
      name: 'admin-accept-invite',
      component: () => import('../views/login/AcceptAdminInviteView.vue')
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: () => import('../views/PrivacyPolicyView.vue')
    },
    {
      path: '/support',
      name: 'support',
      component: () => import('../views/SupportView.vue')
    },
    {
      path: '/marketing',
      name: 'marketing',
      component: () => import('../views/MarketingView.vue')
    },
    {
      path: '/delete-account',
      name: 'delete-account',
      component: () => import('../views/DeleteAccountView.vue')
    },
    {
      path: '/',
      component: MainLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'dashboard',
          name: 'dashboard',
          component: () => import('../views/dashboard/DashboardView.vue')
        },
        {
          path: 'admins',
          name: 'admins',
          component: () => import('../views/admin/AdminsView.vue'),
          meta: { requiresRole: 'superadmin' }
        },
        {
          path: 'admins/:id',
          name: 'admin-details',
          component: () => import('../views/admin/AdminDetailView.vue'),
          meta: { requiresRole: 'superadmin' }
        },

        {
          path: 'establishments',
          name: 'establishments',
          component: () => import('../views/establishments/EstablishmentsView.vue')
        },
        {
          path: 'establishments/new',
          name: 'new-establishment',
          component: () => import('../views/establishments/EstablishmentFormView.vue')
        },
        {
          path: 'establishments/:id',
          name: 'establishment-details',
          component: () => import('../views/establishments/EstablishmentDetailsView.vue')
        },
        {
          path: 'establishments/:id/edit',
          name: 'edit-establishment',
          component: () => import('../views/establishments/EstablishmentFormView.vue')
        },
        {
          path: 'establishments/:id/locations/new',
          name: 'new-location',
          component: () => import('../views/locations/LocationFormView.vue')
        },
        {
          path: 'locations',
          name: 'locations',
          component: () => import('../views/locations/LocationsView.vue')
        },
        {
          path: 'locations/new',
          name: 'new-standalone-location',
          component: () => import('../views/locations/LocationFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'locations/:id',
          name: 'location-details',
          component: LocationDetailsView
        },
        {
          path: 'locations/:id/edit',
          name: 'location-edit',
          component: () => import('../views/locations/LocationFormView.vue')
        },
        {
          path: 'venues',
          redirect: '/establishments'
        },
        {
          path: 'venues/new',
          redirect: '/establishments/new'
        },

        {
          path: 'menus',
          name: 'menus',
          component: () => import('../views/menus/MenusView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:establishmentId/menu',
          name: 'establishment-menu',
          component: () => import('../views/menus/EstablishmentMenuView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:establishmentId/menu/categories/new',
          name: 'new-menu-category',
          component: () => import('../views/menus/MenuCategoryFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:establishmentId/menu/categories/:categoryId/edit',
          name: 'edit-menu-category',
          component: () => import('../views/menus/MenuCategoryFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:establishmentId/menu/categories/:categoryId/items/new',
          name: 'new-menu-item',
          component: () => import('../views/menus/MenuItemFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:establishmentId/menu/items/:itemId/edit',
          name: 'edit-menu-item',
          component: () => import('../views/menus/MenuItemFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'news',
          name: 'news',
          component: () => import('../views/news/NewsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'customers',
          name: 'customers',
          component: () => import('../views/customers/CustomersView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'customers/:id',
          name: 'customer-details',
          component: () => import('../views/customers/CustomerDetailView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'promo-codes',
          name: 'promo-codes',
          component: () => import('../views/promo/PromoCodesView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'promo-codes/new',
          name: 'new-promo-code',
          component: () => import('../views/promo/PromoCodeFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'promo-codes/:id/edit',
          name: 'edit-promo-code',
          component: () => import('../views/promo/PromoCodeFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'membership-programs',
          name: 'membership-programs',
          component: () => import('../views/membership/MembershipProgramsView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'membership-programs/new',
          name: 'new-membership-program',
          component: () => import('../views/membership/MembershipProgramFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'membership-programs/:id/edit',
          name: 'edit-membership-program',
          component: () => import('../views/membership/MembershipProgramFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'membership-programs/:id',
          name: 'membership-program-details',
          component: () => import('../views/membership/MembershipProgramDetailView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'news/new',
          name: 'new-news',
          component: () => import('../views/news/NewsFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'news/:id',
          name: 'news-details',
          component: () => import('../views/news/NewsDetailView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'news/:id/edit',
          name: 'edit-news',
          component: () => import('../views/news/NewsFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:id/news',
          name: 'establishment-news',
          component: () => import('../views/establishments/EstablishmentDetailsView.vue'),
          props: { activeTab: 'news' },
          meta: { requiresAuth: true }
        },
        {
          path: 'establishments/:establishmentId/news/new',
          name: 'new-establishment-news',
          component: () => import('../views/news/NewsFormView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'rewards',
          name: 'rewards',
          component: () => import('../views/rewards/RewardsView.vue')
        },
        {
          path: 'rewards/create',
          name: 'rewards-create',
          component: () => import('../views/rewards/RewardFormView.vue')
        },
        {
          path: 'rewards/:id/edit',
          name: 'rewards-edit',
          component: () => import('../views/rewards/RewardFormView.vue')
        },
        {
          path: '/locations/:locationId/tables',
          name: 'tables',
          component: TablesView
        },
        {
          path: 'office-admins',
          name: 'office-admins',
          component: () => import('../views/office-admin/OfficeAdminsView.vue')
        },
        {
          path: 'office-admins/new',
          name: 'new-office-admin',
          component: () => import('../views/office-admin/OfficeAdminFormView.vue')
        },
        {
          path: 'office-admins/:id',
          name: 'office-admin-details',
          component: () => import('../views/office-admin/OfficeAdminDetailView.vue')
        },
        {
          path: 'office-admins/:id/edit',
          name: 'edit-office-admin',
          component: () => import('../views/office-admin/OfficeAdminFormView.vue')
        },
        {
          path: 'profile',
          name: 'profile',
          component: () => import('../views/profile/ProfileView.vue'),
          meta: { requiresAuth: true }
        }
      ]
    }
  ]
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const auth = useAuthStore()
  const projectContext = useProjectContextStore()

  if (to.meta.requiresAuth) {
    const sessionOk = await auth.ensureSession()
    if (!sessionOk) {
      return '/login'
    }
  }

  const isAuthenticated = auth.isAuthenticated()

  if (to.path === '/login') {
    const sessionOk = await auth.ensureSession()
    if (sessionOk) {
      if (
        auth.user?.role === 'superadmin' &&
        projectContext.projectsLoaded &&
        projectContext.projects.length === 0
      ) {
        return { path: '/admins', query: { tab: 'projects' } }
      }
      return '/dashboard'
    }
    return true
  }

  if (isAuthenticated && !auth.user) {
    await auth.fetchUserProfile()
    if (!auth.user) {
      return '/login'
    }
  }

  if (to.meta.requiresRole && auth.user?.role !== to.meta.requiresRole) {
    return '/dashboard'
  }

  const isProjectSelectionExempt =
    to.path === '/dashboard' ||
    to.path === '/profile' ||
    to.path === '/admins' ||
    to.path.startsWith('/admins/')

  const requiresExplicitProjectSelection = auth.user?.role === 'superadmin'
  const superadminMustPickProject =
    isAuthenticated &&
    requiresExplicitProjectSelection &&
    !isProjectSelectionExempt &&
    projectContext.projectsLoaded &&
    projectContext.projects.length > 0 &&
    !projectContext.activeProjectId

  if (superadminMustPickProject) {
    return '/dashboard'
  }

  const superadminProjectOnboarding =
    isAuthenticated &&
    auth.user?.role === 'superadmin' &&
    projectContext.projectsLoaded &&
    projectContext.projects.length === 0

  const onboardingAllowedPath =
    to.path === '/profile' ||
    to.path === '/admins' ||
    to.path.startsWith('/admins/')

  if (superadminProjectOnboarding && !onboardingAllowedPath) {
    return { path: '/admins', query: { tab: 'projects' } }
  }

  const adminWithoutProject =
    isAuthenticated &&
    auth.user?.role === 'admin' &&
    projectContext.projectsLoaded &&
    projectContext.projects.length === 0

  if (adminWithoutProject && to.path !== '/dashboard') {
    return '/dashboard'
  }

  const restrictedProjectRole =
    projectContext.activeProjectMembershipRole === 'contributor' ||
    projectContext.activeProjectMembershipRole === 'viewer'

  if (isAuthenticated && to.path.startsWith('/complexes')) {
    return '/dashboard'
  }

  const noCustomers =
    isAuthenticated &&
    auth.user?.role === 'admin' &&
    restrictedProjectRole &&
    to.path.startsWith('/customers')

  if (noCustomers) {
    return '/dashboard'
  }

  const adminLicenseInvalid =
    isAuthenticated &&
    auth.user?.role === 'admin' &&
    !evaluateLicenseStatus(auth.user.license).isValid &&
    to.path !== '/profile'

  if (adminLicenseInvalid && to.path !== '/dashboard') {
    return '/dashboard'
  }
})

export default router